import { scrollTo, mediaQueries } from "../utilities/utils"

// Thumbnail -> gallery scroll (for column/grid galleries, where Swiper's
// native `thumbs` linkage isn't active). Matches by media id so it stays
// correct even when variant selection reorders the gallery media.
function setupThumbnailGalleryScroll() {
  document.querySelectorAll(".main-product__media-wrapper").forEach((wrapper) => {
    const thumbnails = wrapper.querySelector(".swiper-thumbnails")
    const gallery = wrapper.querySelector(".main-product__media-gallery swiper-container")

    if (!thumbnails || !gallery) return

    thumbnails.addEventListener("click", (event) => {
      const slide = event
        .composedPath()
        .find((node) => node instanceof Element && node.matches("swiper-slide[data-id]"))

      if (!slide || !thumbnails.contains(slide)) return

      // When the main gallery is an active slider, Swiper's thumbs option
      // already drives navigation, so let it handle the click.
      const isActiveSlider = gallery.swiper && !gallery.swiper.destroyed
      if (isActiveSlider) return

      const mediaId = slide.getAttribute("data-id")
      const target = gallery.querySelector(
        `.main-product__gallery-item[data-id="${mediaId}"]`
      )

      if (target) {
        scrollTo(target, 16)
      }
    })
  })
}

window.addEventListener("load", setupThumbnailGalleryScroll)

if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", setupThumbnailGalleryScroll)
}

// PhotoSwipe gallery lightbox

let photoSwipePromise = null
const loadPhotoSwipe = () => (photoSwipePromise ??= import("photoswipe"))

// Material Symbols glyphs (same icon font the theme uses via component-icon)
const pswpIcons = {
  closeSVG: '<span class="icon" role="presentation">close</span>',
  arrowPrevSVG: '<span class="icon" role="presentation">chevron_left</span>',
  arrowNextSVG: '<span class="icon" role="presentation">chevron_right</span>',
  zoomSVG:
    '<span class="icon pswp__icn--zoom-in" role="presentation">zoom_in</span><span class="icon pswp__icn--zoom-out" role="presentation">zoom_out</span>',
}

// Collect lightbox items from the live gallery DOM. Rebuilt on every open so
// variant-driven reorders/visibility changes stay in sync. Only image slides
// carry data-pswp-src; video/model/external_video slides are excluded.
function buildLightboxItems(galleryEl) {
  const slides = Array.from(galleryEl.querySelectorAll("swiper-slide.main-product__gallery-item"))
  const items = []
  const slideIndexByItem = []
  const itemIndexBySlide = new Map()

  slides.forEach((slide, slideIndex) => {
    if (slide.classList.contains("u-hide")) return
    const figure = slide.querySelector("figure[data-pswp-src]")
    if (!figure) return

    itemIndexBySlide.set(slideIndex, items.length)
    slideIndexByItem.push(slideIndex)
    items.push({
      src: figure.dataset.pswpSrc,
      width: parseInt(figure.dataset.pswpWidth, 10),
      height: parseInt(figure.dataset.pswpHeight, 10),
      alt: figure.querySelector("img")?.alt || "",
    })
  })

  return { slides, items, slideIndexByItem, itemIndexBySlide }
}

async function openLightbox(galleryEl, clickedSlide) {
  const { slides, items, slideIndexByItem, itemIndexBySlide } = buildLightboxItems(galleryEl)
  const startIndex = itemIndexBySlide.get(slides.indexOf(clickedSlide))
  if (startIndex === undefined || !items.length) return

  const { default: PhotoSwipe } = await loadPhotoSwipe()
  const pswp = new PhotoSwipe({
    dataSource: items,
    index: startIndex,
    bgOpacity: 1,
    showHideAnimationType: "fade",
    mainClass: "pswp--pdp",
    closeSVG: pswpIcons.closeSVG,
    arrowPrevSVG: pswpIcons.arrowPrevSVG,
    arrowNextSVG: pswpIcons.arrowNextSVG,
    zoomSVG: pswpIcons.zoomSVG,
  })

  // On close, sync the gallery slider to the last-viewed image.
  pswp.on("close", () => {
    const swiper = galleryEl.swiper
    if (swiper && !swiper.destroyed) {
      swiper.slideTo(slideIndexByItem[pswp.currIndex], 0)
    }
  })

  pswp.init()
}

function setGalleryMediaOnClick() {
  const galleryEl = document.querySelector(".zoom-lightbox")
  if (!galleryEl || galleryEl.dataset.pswpBound) return

  const isActiveSlider = galleryEl.swiper && !galleryEl.swiper.destroyed

  if (isActiveSlider) {
    galleryEl.swiper.on("click", function () {
      const clickedSlide = this.clickedSlide
      if (!clickedSlide || !clickedSlide.querySelector("figure[data-pswp-src]")) return
      openLightbox(galleryEl, clickedSlide)
    })
  } else {
    galleryEl.addEventListener("click", (event) => {
      const figure = event.target.closest("figure[data-pswp-src]")
      if (!figure || !galleryEl.contains(figure)) return
      openLightbox(galleryEl, figure.closest("swiper-slide"))
    })
  }

  galleryEl.dataset.pswpBound = "true"
}

window.addEventListener("load", setGalleryMediaOnClick)

if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", () => {
    // Wait for swiper initialization to complete
    setTimeout(setGalleryMediaOnClick, 100)
  })
}

// Sticky Add to Cart
function enableStickyAddToCart() {
  const stickyBar = document.querySelector(".sticky-add-to-cart")
  const mainATC = document.querySelector(".main-product__add-to-cart .button--add-to-cart")
  const mainProductSection = document.querySelector("section.main-product")
  const footer = document.querySelector(".footer-sections")

  if (!stickyBar || !mainATC) return

  // Hide sticky bar initially
  stickyBar.classList.remove("active")

  let isFooterVisible = false
  let isATCVisible = true
  let sectionBottomCleared = false

  const updateStickyBar = () => {
    const isDesktop = mediaQueries.isLargeUp.matches
    const shouldShow = isFooterVisible
      ? false
      : isDesktop
        ? sectionBottomCleared
        : !isATCVisible

    if (shouldShow) {
      stickyBar.classList.add("active")
    } else {
      stickyBar.classList.remove("active")
    }
  }

  const checkSectionBottomCleared = () => {
    if (!mainProductSection) return
    const stickyZoneFromBottom = stickyBar.offsetHeight + 16
    const sectionRect = mainProductSection.getBoundingClientRect()
    sectionBottomCleared = sectionRect.bottom <= window.innerHeight - stickyZoneFromBottom
    updateStickyBar()
  }

  // On desktop: show sticky only when section bottom has scrolled above the sticky zone (no overlap)
  let scrollTicking = false
  const onScrollOrResize = () => {
    if (scrollTicking) return
    scrollTicking = true
    requestAnimationFrame(() => {
      if (mediaQueries.isLargeUp.matches) checkSectionBottomCleared()
      else updateStickyBar()
      scrollTicking = false
    })
  }

  if (mainProductSection) {
    window.addEventListener("scroll", onScrollOrResize, { passive: true })
    window.addEventListener("resize", onScrollOrResize)
    checkSectionBottomCleared()
  }

  // Observer for footer - hide sticky bar when footer enters viewport
  if (footer) {
    const footerObserver = new window.IntersectionObserver(
      ([entry]) => {
        isFooterVisible = entry.isIntersecting
        updateStickyBar()
      },
      {
        root: null,
        threshold: 0,
        rootMargin: "0px 0px -100px 0px",
      }
    )
    footerObserver.observe(footer)
  }

  // Observer for main ATC button - show sticky bar when scrolled past (mobile only)
  const atcObserver = new window.IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        isATCVisible = true
      } else {
        isATCVisible = entry.boundingClientRect.top > 0
      }
      updateStickyBar()
    },
    {
      root: null,
      threshold: 0,
    }
  )
  atcObserver.observe(mainATC)

  // Scroll to ATC functionality
  const scrollATC = document.querySelectorAll(".scroll-atc")
  if (scrollATC) {
    scrollATC.forEach((button) => {
      button.addEventListener("click", function (e) {
        const elem = document.querySelector(".main-product__add-to-cart")
        const variantSelects = document.querySelector(".main-product__variant-selector")

        const scrollToElement = variantSelects || elem

        if (scrollToElement) {
          const rect = scrollToElement.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          // Scroll so the element appears 200px below the top of the viewport
          const scrollY = rect.top + scrollTop - 200;

          window.scrollTo({
            top: scrollY,
            behavior: 'smooth'
          });
        }

        if (variantSelects) {
          setTimeout(function() {
            variantSelects.classList.add('ux-shake');
            setTimeout(function() {
              variantSelects.classList.remove('ux-shake');
            }, 800);
          }, 800);
        }
      })
    })
  }
}

window.addEventListener("load", enableStickyAddToCart)

if (Shopify.designMode) {
  document.addEventListener("shopify:section:load", enableStickyAddToCart)
}